import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton, Grid, Divider, TableCell, TableRow, Table, TableBody, Box } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageProductTable from '../tabels/StageProductTable';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey, red, green } from '@mui/material/colors';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import theme from 'assets/theme';
import ProductList from './ProductList';

import { useVisibility } from 'context/visibilityContext';
import { filterDiffsByDisplayColumns } from 'utils/visibilityFilter';
import DiffRenderer from './DiffRenderer';

const StyledTableRow = styled(TableRow)(({ theme, selected }) => ({
  transition: 'background-color 0.3s ease',
  cursor: 'pointer',
  '&:hover > *': {
    backgroundColor: theme.palette.action.hover,
  },
  ...(selected && {
    '& > *': {
      backgroundColor: theme.palette.action.selected,
    },
  }),
}));

function ComponentsReview({ selectedOrg, productData, showSameFields }) {
  // Receive showSameFields
  const [expandedRow, setExpandedRow] = useState(null);
  const [productDataMap, setProductDataMap] = useState({});
  const [expandedOptions, setExpandedOptions] = useState({});
  const [selectedProductId, setSelectedProductId] = useState(null);

  const { visibilityConfig, isLoadingConfig, configError } = useVisibility(); // Get context values

  const featureColumns = [
    { name: 'feature', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];
  const optionColumns = [
    { name: 'option', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];
  const constraintColumns = [
    { name: 'constraint', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  useEffect(() => {
    if (!productData || productData.length === 0) {
      setProductDataMap({});
      return;
    }

    const generatedProductDataMap = {};

    productData.forEach((productDiff) => {
      const generatedFeatureRows = { new: [], changed: [], deleted: [], same: [] };
      const generatedOptionRows = { new: [], changed: [], deleted: [], same: [] };
      const generatedConstraintRows = { new: [], changed: [], deleted: [], same: [] };

      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (productDiff.features[type] || []).forEach((feature) => {
          generatedFeatureRows[type].push({
            id: feature.sfId || feature.id || feature.sf_id || feature.external_id,
            feature: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {feature.name}
              </ArgonTypography>
            ),
            diffs: feature.changes,
            sObjectName: 'SBQQ__ProductFeature__c',
            options: { new: [], changed: [], deleted: [], same: [] },
            type: type,
          });
        });
      });

      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (productDiff.productOptions[type] || []).forEach((option) => {
          const featureId =
            option.feature_id || option.fields?.ProductComponentGroupId || option.fields?.SBQQ__Feature__c;
          const optionData = {
            id: option.sfId || option.id,
            sObjectName: 'SBQQ__ProductOption__c',
            option: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {`${option.product?.name || option.productClassification?.name || option.id}`}
              </ArgonTypography>
            ),
            diffs: option.changes,
            type,
          };

          let addedToFeature = false;
          if (featureId) {
            for (const featureType of ['new', 'changed', 'deleted', 'same']) {
              const feature = generatedFeatureRows[featureType].find((f) => f.id === featureId);
              if (feature) {
                feature.options[type].push(optionData);
                addedToFeature = true;
                break;
              }
            }
          }

          if (!addedToFeature) {
            generatedOptionRows[type].push(optionData);
          }
        });
      });

      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (productDiff.optionConstraints[type] || []).forEach((constraint) => {
          generatedConstraintRows[type].push({
            id: constraint.sfId || constraint.id,
            constraint: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {constraint.name || constraint.fields?.Name || 'Constraint'}
              </ArgonTypography>
            ),
            sObjectName: 'SBQQ__OptionConstraint__c', // VERIFY THIS IS SET
            diffs: constraint.changes,
            type: type,
          });
        });
      });

      generatedProductDataMap[productDiff.product.sfId || productDiff.product.id] = {
        featureRows: generatedFeatureRows,
        optionRows: generatedOptionRows,
        constraintRows: generatedConstraintRows,
        productName: productDiff.product.name,
      };
    });
    setProductDataMap(generatedProductDataMap);

    if (productData.length > 0) {
      setSelectedProductId(productData[0].product.sfId || productData[0].product.id);
    }
  }, [productData]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prevExpandedRow) => (prevExpandedRow === rowId ? null : rowId));
  };

  const handleOptionClick = (optionId) => {
    setExpandedOptions((prevExpandedOptions) => ({
      ...prevExpandedOptions,
      [optionId]: !prevExpandedOptions[optionId],
    }));
  };

  const renderExpandedContent = (row) => {
    const colSpan = row.options
      ? featureColumns.length
      : row.constraint
      ? constraintColumns.length
      : optionColumns.length;
    return (
      <TableRow style={{ width: '100%', padding: '0px' }}>
        <TableCell colSpan={colSpan} style={{ padding: '0px' }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit style={{ width: '100%' }}>
            <ArgonBox p={3} color="text" bgcolor={grey[50]}>
              <DiffRenderer
                changes={row.diffs}
                sObjectName={row.sObjectName}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />
              {row.options &&
                (row.options.new.length > 0 ||
                  row.options.changed.length > 0 ||
                  row.options.deleted.length > 0 ||
                  row.options.same.length > 0) && (
                  <ArgonBox mt={2}>
                    <Table>
                      <TableBody>
                        {['new', 'changed', 'deleted', 'same'].map(
                          (type) =>
                            row.options[type].length > 0 && (
                              <React.Fragment key={type}>
                                <ArgonTypography variant="h6" fontWeight="medium" mb={1}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)} Options
                                </ArgonTypography>
                                {row.options[type].map((option) => (
                                  <StyledTableRow key={option.id} selected={expandedOptions[option.id]}>
                                    {renderOptionRow(option)}
                                  </StyledTableRow>
                                ))}
                              </React.Fragment>
                            )
                        )}
                      </TableBody>
                    </Table>
                  </ArgonBox>
                )}
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderOptionRow = (option) => {
    return (
      <TableCell style={{ padding: '0px', width: '100%' }}>
        <ArgonBox
          display="flex"
          alignItems="center"
          //onClick={() => handleOptionClick(option.id)}
          //style={{ cursor: 'pointer' }}
          p={1}
        >
          <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
            {option.option}
          </ArgonTypography>
          {/* <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOptionClick(option.id);
            }}
          >
            <Icon>{expandedOptions[option.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton> */}
        </ArgonBox>
        {/* <Collapse in={expandedOptions[option.id]} timeout="auto" unmountOnExit>
          <ArgonBox p={2} bgcolor={grey[100]}>
            {renderDiffContent(option.diffs)}
          </ArgonBox>
        </Collapse> */}
      </TableCell>
    );
  };

  const renderOptionExpandedContent = (row) => {
    return (
      <TableRow style={{ width: '100%', padding: '0px' }}>
        <TableCell colSpan={optionColumns.length} style={{ padding: '0px' }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit style={{ width: '100%' }}>
            <ArgonBox p={3} color="text" bgcolor={grey[50]}>
              <DiffRenderer
                changes={row.diffs}
                sObjectName={row.sObjectName}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderProductSection = (productName, productData) => {
    const { featureRows, optionRows, constraintRows } = productData;

    const renderTableSection = (title, rows, columns, renderRowSubComponent) => {
      if (!rows || rows.length === 0) {
        return null;
      }
      return (
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                {title}
              </ArgonTypography>
              <ArgonBox
                sx={{
                  '& .MuiTableRow-root:not(:last-child)': {
                    '& td': {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {renderRowSubComponent ? (
                  <StageExpandableTable
                    rows={rows}
                    columns={columns}
                    renderRowSubComponent={({ row }) => renderRowSubComponent(row)}
                  />
                ) : (
                  <StageProductTable rows={rows} columns={columns} />
                )}
              </ArgonBox>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    };

    const formattedRows = (rows) => {
      return rows.map((row) => ({
        ...row,
        renderExpandedContent: () => {
          if (row.options) {
            return renderExpandedContent(row);
          } else if (row.constraint) {
            return renderExpandedContent(row);
          } else {
            return renderOptionExpandedContent(row);
          }
        },
        onClick: () => handleRowClick(row.id),
        selected: expandedRow === row.id,
        actions: (
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.id);
            }}
          >
            <Icon>{expandedRow === row.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        ),
      }));
    };

    const hasComponentFeature = ['new', 'changed', 'deleted', 'same'].some((type) => featureRows[type].length > 0);

    const hasComponentOptions = ['new', 'changed', 'deleted', 'same'].some((type) => optionRows[type].length > 0);

    const hasComponentConstraints = ['new', 'changed', 'deleted', 'same'].some(
      (type) => constraintRows[type].length > 0
    );

    if (!hasComponentFeature && !hasComponentOptions && !hasComponentConstraints) {
      return (
        <ArgonBox key={productName} mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                There is no data available for this product's features, options, and constraints.
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    }

    return (
      <ArgonBox key={productName} mb={3}>
        {['new', 'changed', 'same', 'deleted'].map(
          (type) =>
            featureRows[type].length > 0 &&
            renderTableSection(
              `${type.charAt(0).toUpperCase() + type.slice(1)} Features`,
              formattedRows(featureRows[type]),
              featureColumns,
              (row) => row.renderExpandedContent()
            )
        )}

        {['new', 'changed', 'same', 'deleted'].map(
          (type) =>
            optionRows[type].length > 0 &&
            renderTableSection(
              `${type.charAt(0).toUpperCase() + type.slice(1)} Options`,
              formattedRows(optionRows[type]),
              optionColumns,
              (row) => row.renderExpandedContent()
            )
        )}

        {['new', 'changed', 'same', 'deleted'].map(
          (type) =>
            constraintRows[type].length > 0 &&
            renderTableSection(
              `${type.charAt(0).toUpperCase() + type.slice(1)} Constraints`,
              formattedRows(constraintRows[type]),
              constraintColumns,
              (row) => row.renderExpandedContent()
            )
        )}
      </ArgonBox>
    );
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
  };

  const productList = Object.keys(productDataMap).map((productId) => ({
    id: productId,
    name: productDataMap[productId].productName,
  }));

  return (
    <ArgonBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProductList
            products={productList}
            selectedProductId={selectedProductId}
            onProductSelect={handleProductSelect}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedProductId && productDataMap[selectedProductId] && (
            <>
              {renderProductSection(productDataMap[selectedProductId].productName, productDataMap[selectedProductId])}
            </>
          )}
          {!selectedProductId && (
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                  Select a product to view its components.
                </ArgonTypography>
              </ArgonBox>
            </Card>
          )}
        </Grid>
      </Grid>
    </ArgonBox>
  );
}

ComponentsReview.propTypes = {
  productData: PropTypes.arrayOf(
    PropTypes.shape({
      features: PropTypes.shape({
        new: PropTypes.arrayOf(PropTypes.object).isRequired,
        changed: PropTypes.arrayOf(PropTypes.object).isRequired,
        deleted: PropTypes.arrayOf(PropTypes.object).isRequired,
        same: PropTypes.arrayOf(PropTypes.object).isRequired,
      }).isRequired,
      productOptions: PropTypes.shape({
        new: PropTypes.arrayOf(PropTypes.object).isRequired,
        changed: PropTypes.arrayOf(PropTypes.object).isRequired,
        deleted: PropTypes.arrayOf(PropTypes.object).isRequired,
        same: PropTypes.arrayOf(PropTypes.object).isRequired,
      }).isRequired,
      optionConstraints: PropTypes.shape({
        new: PropTypes.arrayOf(PropTypes.object).isRequired,
        changed: PropTypes.arrayOf(PropTypes.object).isRequired,
        deleted: PropTypes.arrayOf(PropTypes.object).isRequired,
        same: PropTypes.arrayOf(PropTypes.object).isRequired,
      }),
      product: PropTypes.shape({
        name: PropTypes.string,
        sf_id: PropTypes.string,
        external_id: PropTypes.string,
        id: PropTypes.string,
      }),
    })
  ).isRequired,
  showSameFields: PropTypes.bool, // Add showSameFields prop
};

export default ComponentsReview;
